import { zodResolver } from '@hookform/resolvers/zod';
import { useResetPassword } from 'features/auth/hooks/useResetPassword';
import { resetPasswordSchema } from 'features/auth/schemas/resetPassword.schema';
import { useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';

const ResetPassword = () => {
  const { token } = useParams();
  const { mutate: resetPassword, isPending } = useResetPassword(token);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onChange',
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = (data) => {
    resetPassword(data);
  };

  if (!token) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-background px-4'>
        <div className='max-w-md w-full space-y-8 text-center'>
          <h1 className='text-3xl font-bold text-foreground'>Invalid Reset Link</h1>
          <p className='mt-2 text-muted-foreground'>The password reset link is invalid or has expired.</p>
          <Link to='/login' className='inline-flex items-center gap-1 text-primary hover:underline'>
            <Icon name='ArrowLeft' size={14} />
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen flex items-center justify-center bg-background px-4'>
      <div className='max-w-md w-full space-y-8'>
        {/* Header */}
        <div className='text-center'>
          <h1 className='text-3xl font-bold text-foreground'>Reset Password</h1>
          <p className='mt-2 text-muted-foreground'>Enter your new password below</p>
        </div>

        {/* Reset Password Form */}
        <form className='mt-8 space-y-6' onSubmit={handleSubmit(onSubmit)}>
          <div className='space-y-4'>
            {/* Password Field */}
            <div>
              <label htmlFor='password' className='block text-sm font-medium text-foreground mb-1'>
                New Password
              </label>
              <Input
                id='password'
                type='password'
                placeholder='Enter new password'
                disabled={isPending}
                {...register('password')}
              />
              {errors.password && <p className='mt-1 text-sm text-red-500'>{errors.password.message}</p>}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor='confirmPassword' className='block text-sm font-medium text-foreground mb-1'>
                Confirm Password
              </label>
              <Input
                id='confirmPassword'
                type='password'
                placeholder='Confirm new password'
                disabled={isPending}
                {...register('confirmPassword')}
              />
              {errors.confirmPassword && (
                <p className='mt-1 text-sm text-red-500'>{errors.confirmPassword.message}</p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <Button type='submit' className='w-full' disabled={isPending}>
            {isPending ? (
              <>
                <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2' />
                Resetting...
              </>
            ) : (
              <>
                <Icon name='Lock' size={16} className='mr-2' />
                Reset Password
              </>
            )}
          </Button>

          {/* Back to Login Link */}
          <div className='text-center'>
            <Link to='/login' className='text-sm text-primary hover:underline inline-flex items-center gap-1'>
              <Icon name='ArrowLeft' size={14} />
              Back to Login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
